import { useDebugValue, useContext, createContext, memo } from 'react';
import { jsx, Fragment } from 'react/jsx-runtime';

/**
 * Creates a named useContext hook that ensures usage within a Context.Provider
 * @param hookName name of the hook
 * @param Context React Context object
 * @returns a wrapped useContext hook that ensures it is used within a Context.Provider
 */
function createNamedContextHook(hookName, Context) {
    return function useContext$1() {
        useDebugValue(hookName);
        var value = useContext(Context);
        if (!value)
            throw new Error("".concat(hookName, " must be used within ").concat(Context.displayName, ".Provider"));
        return value;
    };
}

function createHookContext(name, useHook, propsAreEqual) {
    var _a;
    var hookName = "use".concat(name);
    var providerName = "".concat(name, "Provider");
    var consumerName = "".concat(name, "Consumer");
    var contextName = "".concat(name, "Context");
    var Context = createNamedContext(contextName);
    var useContext = createNamedContextHook(hookName, Context);
    var Provider = createNamedProvider(Context, useHook, propsAreEqual);
    Provider.displayName = providerName;
    var Consumer = createNamedConsumer(consumerName, useContext);
    return Object.assign([useContext, Provider, Consumer, Context], (_a = {},
        _a[hookName] = useContext,
        _a[providerName] = Provider,
        _a[consumerName] = Consumer,
        _a[contextName] = Context,
        _a));
}

function useSafeContext(hook, context) {
    var value = useContext(context);
    if (!value)
        throw new Error("".concat(hook.name, " must be used within a ").concat(context.displayName, ".Provider"));
    return value;
}

function createNamedContext(name, defaultValue) {
    var NamedContext = createContext(defaultValue);
    NamedContext.displayName = name;
    return NamedContext;
}

/**
 * Creates a named context consumer component by invoking the useContext hook
 * @param name name of the consumer component
 * @param useContext hook to provide the context object
 * @returns context consumer component
 */
function createNamedConsumer(name, useContext) {
    var Consumer = memo(function (_a) {
        var children = _a.children;
        return jsx(Fragment, { children: children(useContext()) });
    });
    Consumer.displayName = name;
    return Consumer;
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

/**
 * Create a named context provider with its value provided by a hook
 * @param Context React Context object
 * @param useHook the hook to provide the context value
 * @returns a Component with wraps a context provider
 */
function createNamedProvider(Context, useHook, propsAreEqual) {
    var Provider = memo(function (props) { return jsx(Context.Provider, __assign({ value: useHook(props) }, { children: props.children })); }, propsAreEqual);
    Provider.displayName = "".concat(Context.displayName, ".ProviderWrapper");
    return Provider;
}

export { createHookContext, createNamedConsumer, createNamedContext, createNamedContextHook, createNamedProvider, useSafeContext };
